import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import StatsTable from "./admin/scoring/StatsTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = [["cycle", "Current cycle"], ["week", "This week"], ["30", "Last 30 days"], ["all", "All-time"]];
const tab = (on) => `px-4 py-2 text-sm font-bold rounded-full border transition-colors ${on ? "bg-[#0B0F19] text-white border-[#0B0F19]" : "border-slate-300 text-slate-600 hover:border-slate-500"}`;
const AGE_LABEL = { "13U & Under": "Younger", "14U & Up": "Older" };
const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

export function LeaderboardBoard({ limit, idPrefix = "lb" }) {
  const [f, setF] = useState({ discipline: "hitting", age_group: "", period: "cycle" });
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); axios.get(`${API}/public/leaderboard`, { params: f }).then((r) => setD(r.data)).catch(() => setD({ rows: [], min_reps: 0, error: true })); }, [f]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const unit = f.discipline === "hitting" ? "At-Bats" : "Pitches";
  const rows = (d ? (limit ? d.rows.slice(0, limit) : d.rows) : []).map((r) => ({ ...r, age_group: AGE_LABEL[r.age_group] ?? (r.age_group ? "" : r.age_group) }));
  return (
    <div data-testid={`${idPrefix}-board`}>
      <div className="flex flex-wrap gap-2">
        {[["hitting", "Hitting Leaderboard"], ["pitching", "Pitching Leaderboard"]].map(([k, l]) => <button key={k} data-testid={`${idPrefix}-tab-${k}`} onClick={() => set("discipline", k)} className={tab(f.discipline === k)}>{l}</button>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <select data-testid={`${idPrefix}-age`} value={f.age_group} onChange={(e) => set("age_group", e.target.value)} className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700">
          <option value="">All athletes</option><option value="13U & Under">Younger</option><option value="14U & Up">Older</option>
        </select>
        {PERIODS.map(([k, l]) => <button key={k} data-testid={`${idPrefix}-period-${k}`} onClick={() => set("period", k)} className={tab(f.period === k)}>{l}</button>)}
      </div>
      {d && f.period !== "all" && <p data-testid={`${idPrefix}-range`} className="mt-3 text-xs font-semibold text-slate-500">{d.range ? `${fmt(d.range[0])} – ${fmt(d.range[1])}` : "All-time (no program cycle completed yet)"}</p>}
      <div className="mt-4">
        {!d && <Loader2 className="animate-spin text-slate-400" />}
        {d && <StatsTable rows={rows} outcomes={d.outcomes || []} discipline={f.discipline} rangeLabel={f.period === "cycle" && !d.range ? "All-time" : PERIODS.find((p) => p[0] === f.period)[1]} idp={idPrefix} />}
      </div>
      {d && <p className="mt-3 text-xs text-slate-500">Ranked by average score per {f.discipline === "hitting" ? "at-bat" : "pitch"}. Athletes need at least {d.min_reps} {unit.toLowerCase()} to be officially ranked. Ties share a rank. Shown only with parent permission.</p>}
    </div>
  );
}

function PreviewCol({ disc, d }) {
  const metric = disc === "hitting" ? ["hard_contact_pct", "Hard contact"] : ["located_strike_pct", "Target location"];
  const rows = (d?.rows || []).filter((r) => r.rank != null).slice(0, 3);
  return (
    <div data-testid={`home-preview-${disc}`} className="rounded-2xl border border-slate-200 p-4">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB]">{disc === "hitting" ? "Hitting" : "Pitching"} · {d?.range ? "Current cycle" : "All-time"}</p>
      {!d && <Loader2 className="mt-3 animate-spin text-slate-300" size={18} />}
      {d && !rows.length && <p data-testid={`home-preview-${disc}-empty`} className="mt-2 text-sm text-slate-500">{disc === "hitting" ? "Every scored swing earns points — from strikeouts to extra-base hits." : "Every pitch is scored on strikes and hitting the intended target."} Rankings appear once athletes complete {d.min_reps} scored {disc === "hitting" ? "swings" : "pitches"}.</p>}
      {rows.map((r) => (
        <div key={`${r.name}-${r.rank}`} className="mt-2 grid grid-cols-[28px_1fr_auto] items-center gap-2 text-sm">
          <span className="font-extrabold text-slate-400">{r.rank}</span>
          <span className="min-w-0 truncate font-bold text-[#0B0F19]">{r.name}<span className="block text-[11px] font-medium text-slate-400">{r.attempts} {disc === "hitting" ? "swings" : "pitches"} · {metric[1]} {r[metric[0]]}%</span></span>
          <span className="text-lg font-extrabold tabular-nums text-[#0B0F19]">{r.avg}</span>
        </div>
      ))}
    </div>
  );
}

// Compact homepage preview — same public endpoint, eligibility and ranking rules as the full leaderboard.
export function LeaderboardPreview() {
  const [d, setD] = useState({});
  useEffect(() => { ["hitting", "pitching"].forEach((k) => axios.get(`${API}/public/leaderboard`, { params: { discipline: k, period: "cycle" } }).then((r) => setD((x) => ({ ...x, [k]: r.data }))).catch(() => setD((x) => ({ ...x, [k]: { rows: [], min_reps: 10 } })))); }, []);
  return (
    <section id="leaderboard" data-testid="home-leaderboard" className="bg-white py-14 md:py-16 px-6 md:px-16 border-t border-slate-100">
      <div className="max-w-5xl mx-auto grid md:grid-cols-[1fr_1.4fr] gap-8 items-center">
        <div>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-[#0B0F19] uppercase">Compete. Measure. Improve.</h2>
          <p className="mt-3 text-base font-medium text-slate-600">Every competitive rep creates feedback. Track results, identify tendencies, and learn how to improve.</p>
          <Link to="/leaderboard" data-testid="home-leaderboard-link" className="mt-5 inline-flex items-center gap-2 rounded-full border-2 border-[#0B0F19] px-6 py-3 text-sm font-extrabold uppercase tracking-wider text-[#0B0F19] hover:bg-[#0B0F19] hover:text-white transition-colors">View live leaderboard <ArrowRight size={16} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 gap-3"><PreviewCol disc="hitting" d={d.hitting} /><PreviewCol disc="pitching" d={d.pitching} /></div>
      </div>
    </section>
  );
}

export function LeaderboardSection() {
  return (
    <section id="leaderboard" data-testid="home-leaderboard" className="bg-white py-20 md:py-28 px-6 md:px-16 border-t border-slate-100">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2563EB]">Compete. Measure. Improve.</p>
        <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0B0F19] uppercase">The Rep Lab Leaderboard</h2>
        <div className="mt-8"><LeaderboardBoard limit={10} idPrefix="home-lb" /></div>
        <Link to="/leaderboard" data-testid="home-leaderboard-link" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#0B0F19] hover:text-[#2563EB]">Full leaderboard <ArrowRight size={16} /></Link>
      </div>
    </section>
  );
}

export default function LeaderboardPage() {
  return (
    <div className="min-h-[100svh] bg-white">
      <header className="border-b border-slate-100"><div className="max-w-5xl mx-auto px-6 h-16 flex items-center"><Link to="/" data-testid="leaderboard-back" className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-[#0B0F19]"><ArrowLeft size={16} /> Rep Lab Baseball</Link></div></header>
      <main className="max-w-5xl mx-auto px-6 py-12">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2563EB]">Compete. Measure. Improve.</p>
        <h1 data-testid="leaderboard-title" className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#0B0F19] uppercase">The Rep Lab Leaderboard</h1>
        <div className="mt-10"><LeaderboardBoard idPrefix="lb" /></div>
      </main>
    </div>
  );
}
